import React, { useCallback, useState } from 'react';
import {
  Alert,
  AlertActionLink,
  Button,
  ClipboardCopyButton,
  CodeBlock,
  CodeBlockAction,
  CodeBlockCode,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Spinner,
  Tooltip,
} from '@patternfly/react-core';
import { EyeSlashIcon, KeyIcon, SyncAltIcon } from '@patternfly/react-icons';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import type { ApiResponseCertificateDownload } from '../../../types/api/ApiResponseCertificate';

interface CertificatePrivateKeyTabProps {
  certificateId: number;
  // Called when an error reveals the local row is stale (409 not issued, or
  // 404 meaning `has_private_key` lags the platform) so the parent can
  // refetch — which also hides this tab when the flag flips to false.
  onCertificateStale: () => void;
}

/**
 * "Private key" tab of the certificate detail page. Unlike the PEM tab,
 * nothing is fetched on mount: the key is retrieved live from the platform
 * (`?include_key=1`) only after an explicit "Load the private key" click,
 * rendered with copy / reload / hide actions, and held solely in this
 * component's state — never in any cache, storage, or log. Hiding the key or
 * navigating to another certificate (the tab is keyed on the certificate id)
 * drops it from state and DOM.
 */
function CertificatePrivateKeyTab({ certificateId, onCertificateStale }: CertificatePrivateKeyTabProps) {
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();
  const [privateKey, setPrivateKey] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<boolean>(false);

  const loadPrivateKey = useCallback(async () => {
    // The stale paths trigger a parent refetch that can flip
    // has_private_key and unmount this tab — destroying the inline Alert
    // before it ever renders. The drawer notification survives that, so the
    // user is never silently dumped back on Overview.
    const failStale = (description: string) => {
      onCertificateStale();
      setError(description);
      addNotification({
        title: 'Could not load the private key',
        srTitle: 'Error alert',
        description,
        variant: 'danger',
      });
      setDrawerOpen(true);
    };

    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch(`/datacenters/certificates/${certificateId}/download?include_key=1`, {
        // Never let the key-bearing response touch the browser's disk cache.
        cache: 'no-store',
      });
      let body: ApiResponseCertificateDownload | null;
      try {
        body = (await response.json()) as ApiResponseCertificateDownload;
      } catch {
        body = null;
      }

      if (body?.status === 'success' && body.data?.download) {
        if (body.data.download.private_key === null) {
          // Contract says a missing key is a 404, not a null — defensive.
          failStale('The platform returned no private key for this certificate.');
          return;
        }
        setPrivateKey(body.data.download.private_key);
        return;
      }

      const message =
        body?.message ?? `Failed to load the private key (HTTP ${`${response.status} ${response.statusText}`.trim()})`;
      // 409: the cert is unissued on the platform while the local row said
      // otherwise. 404: the platform stores no key, so the local
      // has_private_key flag is stale. Both mean "refetch the row" — the
      // refetch hides this tab when the flag flips.
      if (response.status === 409 || response.status === 404) {
        failStale(message);
        return;
      }
      setError(message);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected network error occurred.');
    } finally {
      setLoading(false);
    }
  }, [apiFetch, certificateId, onCertificateStale, addNotification, setDrawerOpen]);

  const hidePrivateKey = useCallback(() => {
    setPrivateKey(null);
    setCopied(false);
  }, []);

  const errorAlert = error !== null && (
    <Alert
      variant="danger"
      isInline
      title="Could not load the private key"
      actionLinks={<AlertActionLink onClick={() => void loadPrivateKey()}>Retry</AlertActionLink>}
      style={{ marginBottom: '1rem' }}
    >
      {error}
    </Alert>
  );

  if (privateKey === null) {
    return (
      <div style={{ marginTop: '1rem' }}>
        {errorAlert}
        <EmptyState titleText="Private key not loaded" icon={KeyIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>
            The private key is stored on the certificate platform and is only retrieved — live — when you explicitly
            ask for it. Nothing has been fetched yet.
          </EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button
                variant="primary"
                icon={<KeyIcon />}
                isLoading={loading}
                isDisabled={loading}
                spinnerAriaValueText="Loading the private key"
                onClick={() => void loadPrivateKey()}
              >
                Load the private key
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      </div>
    );
  }

  return (
    <div style={{ marginTop: '1rem' }}>
      {errorAlert}
      <Alert variant="warning" isInline title="Handle with care" style={{ marginBottom: '1rem' }}>
        Anyone holding this private key can impersonate the certificate. Avoid sharing it, and clear your clipboard
        after use.
      </Alert>
      <CodeBlock
        actions={
          <>
            <CodeBlockAction>
              <ClipboardCopyButton
                id={`certificate-private-key-copy-${certificateId}`}
                aria-label="Copy private key"
                variant="plain"
                onClick={() => {
                  void navigator.clipboard.writeText(privateKey);
                  setCopied(true);
                }}
                exitDelay={copied ? 1500 : 600}
                onTooltipHidden={() => setCopied(false)}
              >
                {copied ? 'Copied' : 'Copy private key'}
              </ClipboardCopyButton>
            </CodeBlockAction>
            <CodeBlockAction>
              <Tooltip content="Reload from the platform">
                <Button
                  variant="plain"
                  aria-label="Reload private key"
                  icon={loading ? <Spinner size="sm" aria-label="Reloading private key" /> : <SyncAltIcon />}
                  isAriaDisabled={loading}
                  onClick={() => void loadPrivateKey()}
                />
              </Tooltip>
            </CodeBlockAction>
            <CodeBlockAction>
              <Tooltip content="Hide the key and drop it from this page">
                <Button
                  variant="plain"
                  aria-label="Hide private key"
                  icon={<EyeSlashIcon />}
                  onClick={hidePrivateKey}
                />
              </Tooltip>
            </CodeBlockAction>
          </>
        }
      >
        <CodeBlockCode>{privateKey.trimEnd()}</CodeBlockCode>
      </CodeBlock>
    </div>
  );
}

export default CertificatePrivateKeyTab;
